import { Title } from '@solidjs/meta'
import { useParams } from '@solidjs/router'
import { type Component, createEffect, createMemo, createResource, createSignal, For, onCleanup, Show } from 'solid-js'
import { createStore, produce, unwrap } from 'solid-js/store'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Drawer from '~/components/Drawer'
import GoBack from '~/components/GoBack'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import { useToast } from '~/components/useToast'
import PageInspector from '~/layers/PageInspector'
import PageRenderer, { WidgetPreview } from '~/layers/PageRenderer'

import { formService } from '~/services/form.service'
import {
  type ColumnType,
  countWidgets,
  createSection,
  createWidget,
  emptyContent,
  normalizeContent,
  type PageContentType,
  type PageSettingsType,
  pageService,
  SECTION_LAYOUTS,
  type SectionStyleType,
  type SectionType,
  WIDGET_CATALOG,
  type WidgetDefinitionType,
  type WidgetPropsType,
  type WidgetType,
  type WidgetTypeType,
  widgetDefinition
} from '~/services/page.service'
import { resolveError } from '~/utils/error'

/** Lo que se arrastra: un widget nuevo de la paleta o uno que ya está puesto. */
type DragPayloadType = { kind: 'new'; type: WidgetTypeType } | { kind: 'move'; widgetId: string; columnId: string }

/** Qué está seleccionado ahora mismo: manda lo que muestra el panel derecho. */
type SelectionType = { kind: 'widget'; id: string } | { kind: 'section'; id: string } | null

const DEVICES = [
  { key: 'desktop', label: 'Escritorio', icon: 'material-symbols-light:desktop-windows-outline', width: '100%' },
  { key: 'tablet', label: 'Tablet', icon: 'material-symbols-light:tablet-mac-outline', width: '820px' },
  { key: 'mobile', label: 'Móvil', icon: 'material-symbols-light:smartphone-outline', width: '400px' }
] as const

type DeviceKeyType = (typeof DEVICES)[number]['key']

/**
 * Tarjeta de la paleta. Vive a nivel de módulo porque se usa dentro de un `For`:
 * declarar componentes dentro de otro componente rompe la reconciliación de Solid.
 */
const PaletteItem: Component<{ definition: WidgetDefinitionType; onAdd: (type: WidgetTypeType) => void; onDragState: (dragging: boolean) => void }> = props => (
  <button
    type="button"
    draggable
    class="w-full flex items-center gap-2 rounded-lg border border-border bg-surface p-2.5 text-left transition-colors hover:border-primary hover:bg-primary-soft/40 cursor-grab active:cursor-grabbing"
    onDragStart={e => {
      e.dataTransfer?.setData('text/plain', JSON.stringify({ kind: 'new', type: props.definition.type } satisfies DragPayloadType))
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'copy'
      props.onDragState(true)
    }}
    onDragEnd={() => props.onDragState(false)}
    onClick={() => props.onAdd(props.definition.type)}
  >
    <Icon icon={props.definition.icon} size={22} class="text-primary shrink-0" />
    <span class="text-sm text-foreground">{props.definition.label}</span>
  </button>
)

/** Zona de soltado entre widgets de una columna. */
const WidgetDropZone: Component<{ active: boolean; dragging: boolean; onEnter: () => void; onLeave: () => void; onDrop: (e: DragEvent) => void }> = props => (
  <div
    class={`transition-all rounded ${props.active ? 'h-10 border-2 border-dashed border-primary bg-primary-soft' : props.dragging ? 'h-3' : 'h-1'}`}
    onDragOver={e => {
      e.preventDefault()
      props.onEnter()
    }}
    onDragLeave={() => props.onLeave()}
    onDrop={e => {
      e.preventDefault()
      // Sin esto el `drop` sube a la columna y se procesaría dos veces.
      e.stopPropagation()
      props.onDrop(e)
    }}
  >
    <Show when={props.active}>
      <p class="text-xs text-primary text-center leading-10">Soltar aquí</p>
    </Show>
  </div>
)

const PageBuilderPage: Component = () => {
  const params = useParams<{ id: string }>()
  const toast = useToast()

  const [documentTitle, setDocumentTitle] = createSignal('Editor de página | Florez Jaramillo')

  const [page] = createResource(
    () => params.id,
    async (id: string) => {
      try {
        const data = await pageService.get(id)
        setDocumentTitle(`${data.name} | Florez Jaramillo`)
        return data
      } catch {
        return null
      }
    }
  )

  /** Formularios publicados: son los que se pueden incrustar en la página. */
  const [forms] = createResource(async () => {
    try {
      return (await formService.list({ limit: 100 })).items
    } catch {
      return []
    }
  })

  const formOptions = createMemo(() => (forms() ?? []).filter(form => form.status !== 'draft').map(form => ({ value: form.slug, title: form.title, subtitle: `/f/${form.slug}` })))

  const [content, setContent] = createStore<PageContentType>(emptyContent())
  const [name, setName] = createSignal('')
  const [slug, setSlug] = createSignal('')
  const [status, setStatus] = createSignal<'draft' | 'published'>('draft')
  const [initialized, setInitialized] = createSignal(false)
  const [dirty, setDirty] = createSignal(false)

  const [selection, setSelection] = createSignal<SelectionType>(null)
  const [device, setDevice] = createSignal<DeviceKeyType>('desktop')
  const [dragging, setDragging] = createSignal(false)
  const [dropTarget, setDropTarget] = createSignal<{ columnId: string; index: number } | null>(null)

  const [isSaving, setIsSaving] = createSignal(false)
  const [showPreview, setShowPreview] = createSignal(false)
  const [showSettings, setShowSettings] = createSignal(false)
  const [showPalette, setShowPalette] = createSignal(false)
  const [showInspector, setShowInspector] = createSignal(false)

  /**
   * Por debajo de xl no caben los paneles laterales y se muestran en cajones.
   * Hay que saberlo aquí: en escritorio el panel ya está a la vista, y abrir el
   * cajón al seleccionar dejaba un cajón vacío encima de la página.
   */
  const compactQuery = window.matchMedia('(max-width: 1279px)')
  const [isCompact, setIsCompact] = createSignal(compactQuery.matches)
  const onCompactChange = (e: MediaQueryListEvent) => setIsCompact(e.matches)
  compactQuery.addEventListener('change', onCompactChange)
  onCleanup(() => compactQuery.removeEventListener('change', onCompactChange))

  /** Sólo se abre el cajón de propiedades donde el panel no está visible. */
  const revealInspector = () => {
    if (isCompact()) setShowInspector(true)
  }

  // Se espera a la página: pintar el lienzo vacío antes de tiempo y guardar
  // encima borraría el contenido.
  createEffect(() => {
    if (initialized() || page.loading) return
    const data = page()
    if (!data) return
    const normalized = normalizeContent(data.nodes)
    setContent(normalized.sections.length ? normalized : emptyContent())
    setName(data.name)
    setSlug(data.slug)
    setStatus(data.status)
    setInitialized(true)
  })

  const touch = () => setDirty(true)

  // ── Búsquedas dentro del árbol ───────────────────────────────────────────
  const findWidget = (id: string): WidgetType | null => {
    for (const section of content.sections) {
      for (const column of section.columns) {
        const widget = column.widgets.find(w => w.id === id)
        if (widget) return widget
      }
    }
    return null
  }

  const selectedWidget = createMemo(() => {
    const current = selection()
    return current?.kind === 'widget' ? findWidget(current.id) : null
  })

  const selectedSection = createMemo(() => {
    const current = selection()
    if (current?.kind === 'section') return content.sections.find(s => s.id === current.id) ?? null
    if (current?.kind === 'widget') {
      return content.sections.find(section => section.columns.some(column => column.widgets.some(w => w.id === current.id))) ?? null
    }
    return null
  })

  // ── Mutaciones ───────────────────────────────────────────────────────────
  const insertWidget = (columnId: string, index: number, widget: WidgetType) => {
    setContent(
      produce(draft => {
        for (const section of draft.sections) {
          const column = section.columns.find(c => c.id === columnId)
          if (column) {
            column.widgets.splice(Math.min(index, column.widgets.length), 0, widget)
            return
          }
        }
      })
    )
    setSelection({ kind: 'widget', id: widget.id })
    touch()
  }

  const moveWidget = (widgetId: string, fromColumnId: string, toColumnId: string, index: number) => {
    setContent(
      produce(draft => {
        let moved: WidgetType | undefined
        for (const section of draft.sections) {
          const column = section.columns.find(c => c.id === fromColumnId)
          if (!column) continue
          const position = column.widgets.findIndex(w => w.id === widgetId)
          if (position >= 0) [moved] = column.widgets.splice(position, 1)
          // Soltarlo más abajo en su propia columna: al quitarlo, todo lo que
          // venía después sube una posición.
          if (moved && fromColumnId === toColumnId && position < index) index -= 1
          break
        }
        if (!moved) return
        for (const section of draft.sections) {
          const column = section.columns.find(c => c.id === toColumnId)
          if (column) {
            column.widgets.splice(Math.max(0, Math.min(index, column.widgets.length)), 0, moved)
            return
          }
        }
      })
    )
    touch()
  }

  const removeWidget = (widgetId: string) => {
    setContent(
      produce(draft => {
        for (const section of draft.sections) {
          for (const column of section.columns) {
            const index = column.widgets.findIndex(w => w.id === widgetId)
            if (index >= 0) {
              column.widgets.splice(index, 1)
              return
            }
          }
        }
      })
    )
    if (selection()?.kind === 'widget' && selection()?.id === widgetId) setSelection(null)
    touch()
  }

  const duplicateWidget = (widgetId: string) => {
    setContent(
      produce(draft => {
        for (const section of draft.sections) {
          for (const column of section.columns) {
            const index = column.widgets.findIndex(w => w.id === widgetId)
            if (index >= 0) {
              const copy = structuredClone(unwrap(column.widgets[index]))
              copy.id = `w_${Math.random().toString(36).slice(2, 9)}`
              column.widgets.splice(index + 1, 0, copy)
              return
            }
          }
        }
      })
    )
    touch()
  }

  const patchWidget = (patch: WidgetPropsType) => {
    const current = selection()
    if (current?.kind !== 'widget') return
    setContent(
      produce(draft => {
        for (const section of draft.sections) {
          for (const column of section.columns) {
            const widget = column.widgets.find(w => w.id === current.id)
            if (widget) {
              Object.assign(widget.props, patch)
              return
            }
          }
        }
      })
    )
    touch()
  }

  const addSection = (spans: number[]) => {
    const section = createSection(spans)
    setContent(produce(draft => draft.sections.push(section)))
    setSelection({ kind: 'section', id: section.id })
    touch()
  }

  const removeSection = (sectionId: string) => {
    setContent(
      produce(draft =>
        draft.sections.splice(
          draft.sections.findIndex(s => s.id === sectionId),
          1
        )
      )
    )
    setSelection(null)
    touch()
  }

  const duplicateSection = (sectionId: string) => {
    setContent(
      produce(draft => {
        const index = draft.sections.findIndex(s => s.id === sectionId)
        if (index < 0) return
        const suffix = Math.random().toString(36).slice(2, 9)
        const copy = structuredClone(unwrap(draft.sections[index]))
        copy.id = `s_${suffix}`
        copy.columns = copy.columns.map((column, ci) => ({
          ...column,
          id: `c_${suffix}_${ci}`,
          widgets: column.widgets.map((widget, wi) => ({ ...widget, id: `w_${suffix}_${ci}_${wi}` }))
        }))
        draft.sections.splice(index + 1, 0, copy)
      })
    )
    touch()
  }

  const moveSection = (sectionId: string, direction: -1 | 1) => {
    setContent(
      produce(draft => {
        const index = draft.sections.findIndex(s => s.id === sectionId)
        const target = index + direction
        if (index < 0 || target < 0 || target >= draft.sections.length) return
        const [section] = draft.sections.splice(index, 1)
        draft.sections.splice(target, 0, section)
      })
    )
    touch()
  }

  const patchSection = (patch: SectionStyleType) => {
    const section = selectedSection()
    if (!section) return
    setContent(
      produce(draft => {
        const target = draft.sections.find(s => s.id === section.id)
        if (target) Object.assign(target.style, patch)
      })
    )
    touch()
  }

  const patchPage = (patch: PageSettingsType) => {
    setContent(produce(draft => Object.assign(draft.settings, patch)))
    touch()
  }

  /** Añade el widget donde tenga sentido: la columna seleccionada o la primera. */
  const addWidgetFromPalette = (type: WidgetTypeType) => {
    const section = selectedSection() ?? content.sections[0]
    if (!section) {
      toast.addToast({ title: 'Añade primero una sección', type: 'warning' })
      return
    }
    const current = selection()
    const column = (current?.kind === 'widget' ? section.columns.find(c => c.widgets.some(w => w.id === current.id)) : null) ?? section.columns[0]
    if (!column) return
    insertWidget(column.id, column.widgets.length, createWidget(type))
    setShowPalette(false)
  }

  // ── Arrastrar y soltar ───────────────────────────────────────────────────
  const readPayload = (e: DragEvent): DragPayloadType | null => {
    try {
      const raw = e.dataTransfer?.getData('text/plain')
      return raw ? (JSON.parse(raw) as DragPayloadType) : null
    } catch {
      return null
    }
  }

  const handleDrop = (e: DragEvent, columnId: string, index: number) => {
    const payload = readPayload(e)
    setDragging(false)
    setDropTarget(null)
    if (!payload) return
    if (payload.kind === 'new') insertWidget(columnId, index, createWidget(payload.type))
    else moveWidget(payload.widgetId, payload.columnId, columnId, index)
  }

  // ── Guardado ─────────────────────────────────────────────────────────────
  const save = async (publish = false) => {
    setIsSaving(true)
    try {
      await pageService.update(params.id, {
        name: name().trim() || 'Página sin nombre',
        slug: slug().trim(),
        nodes: unwrap(content)
      })
      if (publish) {
        const updated = await pageService.publish(params.id)
        setStatus(updated.status)
        setSlug(updated.slug)
      }
      setDirty(false)
      toast.addToast({ title: publish ? 'Página publicada' : 'Cambios guardados', type: 'success' })
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsSaving(false)
    }
  }

  const deviceWidth = createMemo(() => DEVICES.find(d => d.key === device())?.width ?? '100%')

  const paletteGroups = createMemo(() => {
    const groups = new Map<string, WidgetDefinitionType[]>()
    for (const definition of WIDGET_CATALOG) {
      groups.set(definition.group, [...(groups.get(definition.group) ?? []), definition])
    }
    return [...groups.entries()]
  })

  // ── Piezas del lienzo ────────────────────────────────────────────────────
  const columnBody = (column: ColumnType) => (
    <div
      class="min-h-16 rounded-lg"
      onDragOver={e => e.preventDefault()}
      onDrop={e => {
        e.preventDefault()
        handleDrop(e, column.id, column.widgets.length)
      }}
    >
      <Show when={column.widgets.length === 0}>
        <div class="flex h-24 items-center justify-center rounded-lg border-2 border-dashed border-border text-xs text-foreground-subtle">Arrastra un elemento aquí</div>
      </Show>

      <For each={column.widgets}>
        {(widget, index) => (
          <>
            <WidgetDropZone
              active={dropTarget()?.columnId === column.id && dropTarget()?.index === index()}
              dragging={dragging()}
              onEnter={() => setDropTarget({ columnId: column.id, index: index() })}
              onLeave={() => setDropTarget(current => (current?.columnId === column.id && current.index === index() ? null : current))}
              onDrop={e => handleDrop(e, column.id, index())}
            />
            <div
              class={`group relative rounded-lg border-2 p-2 transition-colors ${
                selection()?.kind === 'widget' && selection()?.id === widget.id ? 'border-primary' : 'border-transparent hover:border-primary/40'
              }`}
              onClick={e => {
                e.stopPropagation()
                setSelection({ kind: 'widget', id: widget.id })
                revealInspector()
              }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault()
                e.stopPropagation()
                handleDrop(e, column.id, index())
              }}
            >
              {/* Barra del widget: mover, duplicar y borrar sin salir del lienzo. */}
              <div class="absolute -top-3 right-2 z-10 hidden items-center gap-0.5 rounded-lg border border-border bg-surface px-1 py-0.5 shadow-sm group-hover:flex">
                <span
                  draggable
                  class="cursor-grab active:cursor-grabbing p-1 text-foreground-subtle"
                  title="Mover"
                  onDragStart={e => {
                    e.dataTransfer?.setData('text/plain', JSON.stringify({ kind: 'move', widgetId: widget.id, columnId: column.id } satisfies DragPayloadType))
                    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
                    setDragging(true)
                  }}
                  onDragEnd={() => {
                    setDragging(false)
                    setDropTarget(null)
                  }}
                >
                  <Icon icon="material-symbols-light:drag-indicator" size={18} />
                </span>
                <Button size="xs" variant="text" color="info" tooltip="Duplicar" icon="material-symbols-light:content-copy-outline" onClick={() => duplicateWidget(widget.id)} />
                <Button size="xs" variant="text" color="danger" tooltip="Quitar" icon="material-symbols-light:delete-outline" onClick={() => removeWidget(widget.id)} />
              </div>

              {/* Sin interacción: en el lienzo se diseña, no se navega. */}
              <div class="pointer-events-none">
                <WidgetPreview widget={widget} interactive={false} />
              </div>
            </div>
          </>
        )}
      </For>

      <WidgetDropZone
        active={dropTarget()?.columnId === column.id && dropTarget()?.index === column.widgets.length}
        dragging={dragging()}
        onEnter={() => setDropTarget({ columnId: column.id, index: column.widgets.length })}
        onLeave={() => setDropTarget(null)}
        onDrop={e => handleDrop(e, column.id, column.widgets.length)}
      />
    </div>
  )

  const sectionWrapper = (section: SectionType, rendered: () => unknown) => (
    <div
      class={`group/section relative border-2 transition-colors ${selection()?.kind === 'section' && selection()?.id === section.id ? 'border-primary' : 'border-transparent hover:border-primary/30'}`}
      onClick={() => {
        setSelection({ kind: 'section', id: section.id })
        revealInspector()
      }}
    >
      <div class="absolute left-2 top-2 z-10 hidden items-center gap-0.5 rounded-lg border border-border bg-surface px-1 py-0.5 shadow-sm group-hover/section:flex">
        <span class="px-2 text-xs font-medium text-foreground-subtle">Sección</span>
        <Button size="xs" variant="text" color="secondary" tooltip="Subir" icon="material-symbols-light:arrow-upward" onClick={() => moveSection(section.id, -1)} />
        <Button size="xs" variant="text" color="secondary" tooltip="Bajar" icon="material-symbols-light:arrow-downward" onClick={() => moveSection(section.id, 1)} />
        <Button size="xs" variant="text" color="info" tooltip="Duplicar" icon="material-symbols-light:content-copy-outline" onClick={() => duplicateSection(section.id)} />
        <Button size="xs" variant="text" color="danger" tooltip="Eliminar sección" icon="material-symbols-light:delete-outline" onClick={() => removeSection(section.id)} />
      </div>
      {rendered() as never}
    </div>
  )

  const inspector = () => (
    <PageInspector
      widget={selectedWidget()}
      sectionStyle={selection()?.kind === 'section' ? (selectedSection()?.style ?? null) : null}
      pageSettings={content.settings}
      formOptions={formOptions()}
      onPatchWidget={patchWidget}
      onPatchSection={patchSection}
      onPatchPage={patchPage}
      onError={message => toast.addToast({ title: message, type: 'warning' })}
    />
  )

  const palette = () => (
    <div class="space-y-5">
      <div class="space-y-2">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">Secciones</h3>
        <div class="grid grid-cols-2 gap-2">
          <For each={SECTION_LAYOUTS}>
            {layout => (
              <button type="button" class="rounded-lg border border-border bg-surface p-2 text-xs text-foreground transition-colors hover:border-primary" onClick={() => addSection(layout.spans)}>
                <div class="mb-1 flex gap-0.5">
                  <For each={layout.spans}>{span => <div class="h-3 rounded-sm bg-primary/30" style={{ flex: String(span) }} />}</For>
                </div>
                {layout.label}
              </button>
            )}
          </For>
        </div>
      </div>

      <For each={paletteGroups()}>
        {([group, definitions]) => (
          <div class="space-y-2">
            <h3 class="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">{group}</h3>
            <div class="space-y-1.5">
              <For each={definitions}>{definition => <PaletteItem definition={definition} onAdd={addWidgetFromPalette} onDragState={setDragging} />}</For>
            </div>
          </div>
        )}
      </For>
    </div>
  )

  return (
    <>
      <Title>{documentTitle()}</Title>

      <div class="flex h-[calc(100dvh-4rem)] flex-col">
        {/* Barra superior */}
        <div class="flex flex-wrap items-center gap-2 border-b border-border bg-surface px-3 py-2">
          <GoBack label="Volver a páginas" fallbackPath="/admin/builder/paginas" />
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <p class="truncate font-semibold text-foreground">{name() || 'Página sin nombre'}</p>
              <Badge color={status() === 'published' ? 'success' : 'warning'}>{status() === 'published' ? 'Publicada' : 'Borrador'}</Badge>
              <Show when={dirty()}>
                <span class="text-xs text-foreground-subtle">· sin guardar</span>
              </Show>
            </div>
            <p class="truncate text-xs text-foreground-subtle">/p/{slug()}</p>
          </div>

          <div class="hidden items-center gap-0.5 rounded-lg border border-border p-0.5 md:flex">
            <For each={DEVICES}>
              {item => (
                <Button
                  size="sm"
                  variant={device() === item.key ? 'contained' : 'text'}
                  color={device() === item.key ? 'primary' : 'secondary'}
                  tooltip={item.label}
                  icon={item.icon}
                  onClick={() => setDevice(item.key)}
                />
              )}
            </For>
          </div>

          <Button size="sm" variant="soft" color="secondary" tooltip="Ajustes de la página" icon="material-symbols-light:tune" onClick={() => setShowSettings(true)} />
          <Button size="sm" variant="soft" color="info" icon="material-symbols-light:visibility-outline" onClick={() => setShowPreview(true)}>
            <span class="hidden sm:inline">Previsualizar</span>
          </Button>
          <Button size="sm" variant="soft" color="warning" disabled={isSaving()} icon="material-symbols-light:save-outline" onClick={() => save(false)}>
            <span class="hidden sm:inline">Guardar</span>
          </Button>
          <Button size="sm" color="primary" disabled={isSaving()} icon="material-symbols-light:rocket-launch-outline" onClick={() => save(true)}>
            <span class="hidden sm:inline">Publicar</span>
          </Button>
        </div>

        <div class="flex min-h-0 flex-1">
          {/* Paleta */}
          <aside class="hidden w-64 shrink-0 overflow-y-auto border-r border-border bg-surface p-3 lg:block">{palette()}</aside>

          {/* Lienzo */}
          <main class="min-w-0 flex-1 overflow-y-auto bg-surface-container p-3 sm:p-6" onClick={() => setSelection(null)}>
            <Show when={!initialized()}>
              <p class="py-16 text-center text-foreground-subtle">Cargando página...</p>
            </Show>

            <Show when={initialized()}>
              <div class="mx-auto bg-surface shadow-sm transition-all" style={{ 'max-width': deviceWidth() }}>
                <Show
                  when={content.sections.length > 0}
                  fallback={
                    <div class="p-10 text-center">
                      <Icon icon="material-symbols-light:dashboard-customize-outline" size={44} class="mx-auto mb-3 text-foreground-subtle" />
                      <p class="text-sm text-foreground-subtle">Empieza añadiendo una sección desde la paleta.</p>
                    </div>
                  }
                >
                  <PageRenderer content={content} interactive={false} wrapSection={sectionWrapper} renderColumnBody={columnBody} />
                </Show>

                <div class="border-t border-dashed border-border p-4 text-center">
                  <Button size="sm" variant="soft" color="info" icon="material-symbols-light:add" onClick={() => addSection([12])}>
                    Añadir sección
                  </Button>
                </div>
              </div>
            </Show>
          </main>

          {/* Propiedades */}
          <aside class="hidden w-80 shrink-0 overflow-y-auto border-l border-border bg-surface p-4 xl:block">
            <h2 class="mb-3 text-sm font-semibold text-foreground">
              {selectedWidget() ? (widgetDefinition(selectedWidget()!.type)?.label ?? 'Elemento') : selection()?.kind === 'section' ? 'Sección' : 'Página'}
            </h2>
            {inspector()}
          </aside>
        </div>

        {/* Barra inferior: sólo en móvil y tablet, donde no caben los paneles. */}
        <div class="flex items-center justify-around gap-2 border-t border-border bg-surface px-3 py-2 xl:hidden">
          <Button size="sm" variant="soft" color="info" icon="material-symbols-light:widgets-outline" onClick={() => setShowPalette(true)}>
            Elementos
          </Button>
          <Button size="sm" variant="soft" color="secondary" icon="material-symbols-light:tune" onClick={() => setShowInspector(true)}>
            Propiedades
          </Button>
        </div>
      </div>

      {/* Paleta en móvil */}
      <Drawer isOpen={showPalette()} onClose={() => setShowPalette(false)} position="left" size="sm">
        <div class="space-y-4 p-4">
          <h2 class="font-semibold text-foreground">Elementos</h2>
          {palette()}
        </div>
      </Drawer>

      {/* Propiedades en móvil */}
      <Drawer isOpen={isCompact() && showInspector()} onClose={() => setShowInspector(false)} position="right" size="sm">
        <div class="space-y-4 p-4">
          <h2 class="font-semibold text-foreground">{selectedWidget() ? (widgetDefinition(selectedWidget()!.type)?.label ?? 'Elemento') : selection()?.kind === 'section' ? 'Sección' : 'Página'}</h2>
          {inspector()}
        </div>
      </Drawer>

      {/* Ajustes de la página */}
      <Modal isOpen={showSettings()} onClose={() => setShowSettings(false)} size="md">
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">Ajustes de la página</h2>
          <p class="text-sm text-foreground-subtle">Nombre y dirección pública</p>
        </Modal.Header>
        <Modal.Body>
          <div class="space-y-4">
            <Input
              label="Nombre"
              value={name()}
              onInput={e => {
                setName(e.currentTarget.value)
                touch()
              }}
            />
            <Input
              label="Dirección (slug)"
              value={slug()}
              helperText="La página se verá en /p/tu-slug"
              onInput={e => {
                setSlug(e.currentTarget.value)
                touch()
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="soft" color="secondary" onClick={() => setShowSettings(false)}>
              Cerrar
            </Button>
            <Button
              color="primary"
              onClick={() => {
                setShowSettings(false)
                save(false)
              }}
            >
              Guardar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Previsualización: el mismo renderizador de la página pública */}
      <Modal isOpen={showPreview()} onClose={() => setShowPreview(false)} size="xl">
        <Modal.Header>
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-xl font-bold text-foreground">Previsualización</h2>
              <p class="text-sm text-foreground-subtle">
                {countWidgets(content)} elemento(s) en {content.sections.length} sección(es)
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div class="max-h-[70dvh] overflow-y-auto bg-surface">
            <PageRenderer content={content} interactive={false} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="soft" color="secondary" onClick={() => setShowPreview(false)}>
              Seguir editando
            </Button>
            <Show when={status() === 'published'}>
              <Button variant="soft" color="info" icon="material-symbols-light:open-in-new" onClick={() => window.open(`/p/${slug()}`, '_blank', 'noopener')}>
                Ver publicada
              </Button>
            </Show>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default PageBuilderPage
